import { DatePipe } from '@angular/common';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, distinctUntilChanged, EMPTY, filter, finalize, from, map, mergeMap, Observable, of, Subject, switchMap, tap, throwError } from 'rxjs';
import { documentKind, DocumentKind, isFileDrag, MAX_DOCUMENT_SIZE_BYTES, MAX_DOCUMENT_SIZE_LABEL } from '../../../../shared/constants/document-upload';
import { canAccess, DOCUMENT_DELETE_ROLES } from '../../../../shared/constants/role-access';
import { DeleteConfirmationComponent } from '../../../../shared/modals/delete-confirmation/delete-confirmation.component';
import { DocumentUploadModalComponent } from '../../../../shared/modals/document-upload-modal/document-upload-modal.component';
import { DeleteConfirmationData, DocumentPreviewModalData } from '../../../../shared/models/modal-data.model';
import { IOrderDocumentFile } from '../../../../shared/models/order-document.model';
import { UserRole } from '../../../../shared/models/user.model';
import { AuthService } from '../../../../shared/services/auth.service';
import { ImagePdfConverterService } from '../../../../shared/services/image-pdf-converter.service';
import { ModalService } from '../../../../shared/services/modal.service';
import { OrderDocumentService } from '../../../../shared/services/order-document.service';
import { ToastService } from '../../../../shared/services/toast.service';

// One request per file; at most this many run at once, however many files are dropped.
const UPLOAD_CONCURRENCY = 2;

interface UploadItem {
  key: number;
  name: string;
  progress: number; // 0–100
  converting: boolean; // image → PDF still running
  error: string | null;
}

interface UploadJob {
  key: number;
  orderDocumentId: string;
  scopeKey: string; // the list shown when it was queued, see DocumentsScope
  file: File;
  kind: DocumentKind;
}

// Which documents the tab lists: those of every Auftragsbegleitschein of the linked order, or — with
// no order linked — only the current one's.
interface DocumentsScope {
  kind: 'order' | 'orderDocument';
  id: string;
}

function scopeKey(scope: DocumentsScope | null): string | null {
  return scope ? `${scope.kind}:${scope.id}` : null;
}

class ConversionError extends Error {
  constructor(public readonly userMessage: string) {
    super(userMessage);
  }
}

// The Dokumente tab of an Auftragsbegleitschein: PDFs (mainly the customer's delivery note) stored
// in GridFS and exported to the DMS by the nightly job. With an order linked, it lists the PDFs of
// every Auftragsbegleitschein of that order; uploads always go to the current one, while preview and
// delete go through each file's own Auftragsbegleitschein (orderDocumentId). Every role sees,
// previews and uploads them; ADMIN, BÜRO and WARENEINGANG delete. Once exported to the DMS, only ADMIN may still delete.
// Files are chosen in DocumentUploadModalComponent; JPEG/PNG/SVG images are converted to a PDF in
// the browser before upload, since the backend stores PDFs only.
// Uploads/deletes are immediate API calls, independent of the page's form and "Speichern".
@Component({
  selector: 'app-documents',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './documents.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:dragover)': 'onWindowDrag($event)',
    '(window:drop)': 'onWindowDrag($event)',
  },
})
export class DocumentsComponent {
  private readonly orderDocumentService = inject(OrderDocumentService);
  private readonly authService = inject(AuthService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly imagePdfConverter = inject(ImagePdfConverterService);

  // Null while creating: documents can only be attached to a saved Auftragsbegleitschein.
  public readonly orderDocumentId = input<string | null>(null);
  // The linked order's _id; null when none is linked.
  public readonly orderId = input<string | null>(null);
  // Label per Auftragsbegleitschein of the order, for the owner badge on each row.
  public readonly ownerLabels = input<Record<string, string>>({});
  // The tab is open. Tab panels are only hidden, never destroyed, so the list is fetched on first open.
  public readonly active = input(false);

  public readonly saved = computed(() => !!this.orderDocumentId());
  private readonly scope = computed<DocumentsScope | null>(() => {
    const orderId = this.orderId();
    if (orderId) return { kind: 'order', id: orderId };
    const orderDocumentId = this.orderDocumentId();
    return orderDocumentId ? { kind: 'orderDocument', id: orderDocumentId } : null;
  });
  // The owner badge only tells something apart once the order has more than one Auftragsbegleitschein.
  public readonly showOwner = computed(() => Object.keys(this.ownerLabels()).length > 1);
  private readonly currentUser = toSignal(this.authService.currentUser$, { initialValue: null });
  private readonly isAdmin = computed(() => this.currentUser()?.role === UserRole.ADMIN);
  private readonly mayDelete = computed(() => canAccess(this.currentUser()?.role, DOCUMENT_DELETE_ROLES));
  public readonly maxSizeLabel = MAX_DOCUMENT_SIZE_LABEL;

  private readonly documents = signal<IOrderDocumentFile[]>([]);
  public readonly isLoading = signal(false);
  public readonly uploads = signal<UploadItem[]>([]);

  public readonly rows = computed(() =>
    this.documents().map(doc => ({
      doc,
      canDelete: this.mayDelete() && (!doc.exportDate || this.isAdmin()),
      ownerLabel: this.ownerLabels()[doc.orderDocumentId] ?? '–',
      isCurrentOwner: doc.orderDocumentId === this.orderDocumentId(),
    }))
  );

  private readonly uploadQueue = new Subject<UploadJob>();
  private nextUploadKey = 0;

  constructor() {
    // (Re)load whenever the open tab shows a different scope; switching tabs alone doesn't refetch,
    // and neither does switching between Auftragsbegleitscheine of the same order.
    toObservable(computed(() => (this.active() ? this.scope() : null)))
      .pipe(
        filter((scope): scope is DocumentsScope => !!scope),
        distinctUntilChanged((a, b) => scopeKey(a) === scopeKey(b)),
        switchMap(scope => this.loadDocuments(scope)),
        takeUntilDestroyed()
      )
      .subscribe(documents => this.documents.set(documents));

    this.uploadQueue
      .pipe(
        mergeMap(job => this.upload(job), UPLOAD_CONCURRENCY),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  public openUploadModal(): void {
    if (!this.saved()) return;
    this.modalService
      .open(DocumentUploadModalComponent)
      .then((files: File[]) => this.addFiles(files))
      .catch(() => {});
  }

  // A file dropped beside the upload modal's drop zone would be opened by the browser, leaving the
  // page and any unsaved input. The drop zone handles its own events first (defaultPrevented).
  public onWindowDrag(event: DragEvent): void {
    if (!this.active() || !isFileDrag(event) || event.defaultPrevented) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
  }

  public dismissUpload(key: number): void {
    this.uploads.update(list => list.filter(item => item.key !== key));
  }

  public async openPreview(doc: IOrderDocumentFile): Promise<void> {
    try {
      // Loaded on demand so the PDF viewer (pdf.js) stays out of the page's bundle.
      const { DocumentPreviewModalComponent } = await import(
        '../../../../shared/modals/document-preview-modal/document-preview-modal.component'
      );
      const data: DocumentPreviewModalData = { document: doc, documents: this.documents() };
      this.modalService.open(DocumentPreviewModalComponent, data).catch(() => {});
    } catch {
      this.toastService.showError('Die Vorschau konnte nicht geladen werden');
    }
  }

  public confirmDelete(doc: IOrderDocumentFile): void {
    const data: DeleteConfirmationData = {
      title: 'Dokument löschen',
      message: doc.exportDate
        ? `Möchten Sie „${doc.originalName}“ wirklich löschen? Es wurde bereits ins DMS exportiert und bleibt dort erhalten.`
        : `Möchten Sie „${doc.originalName}“ wirklich löschen?`,
    };
    this.modalService
      .open(DeleteConfirmationComponent, data)
      .then((confirmed: boolean) => {
        if (confirmed) this.deleteDocument(doc);
      })
      .catch(() => {});
  }

  // Through the file's own Auftragsbegleitschein — it may be a sibling of the one shown.
  private deleteDocument(doc: IOrderDocumentFile): void {
    this.orderDocumentService.deleteDocument(doc.orderDocumentId, doc._id).subscribe({
      next: () => {
        this.removeDocument(doc._id);
        this.toastService.showSuccess('Dokument gelöscht');
      },
      error: (error: HttpErrorResponse) => {
        switch (error.status) {
          case 404:
            this.removeDocument(doc._id);
            this.toastService.showInfo('Das Dokument wurde bereits gelöscht');
            break;
          case 423:
            // Exported in the meantime — refresh so the list shows the DMS lock
            this.toastService.showError('Das Dokument ist nach dem DMS-Export gesperrt');
            this.reload();
            break;
          case 409:
            this.toastService.showWarning('Der DMS-Export läuft gerade, bitte später erneut versuchen');
            break;
          case 403:
            this.toastService.showError('Keine Berechtigung zum Löschen');
            break;
          default:
            this.toastService.showError('Das Dokument konnte nicht gelöscht werden');
        }
      },
    });
  }

  private addFiles(files: File[]): void {
    const orderDocumentId = this.orderDocumentId();
    const currentScopeKey = scopeKey(this.scope());
    if (!orderDocumentId || !currentScopeKey) return;

    for (const file of files) {
      const kind = documentKind(file);
      if (!kind) {
        this.toastService.showError(`„${file.name}“ ist kein unterstütztes Format (PDF, JPG, PNG, SVG)`);
        continue;
      }
      if (file.size === 0) {
        this.toastService.showError(`„${file.name}“ ist leer`);
        continue;
      }
      if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
        this.toastService.showError(`„${file.name}“ ist größer als ${MAX_DOCUMENT_SIZE_LABEL}`);
        continue;
      }
      const key = this.nextUploadKey++;
      this.uploads.update(list => [...list, { key, name: file.name, progress: 0, converting: kind === 'image', error: null }]);
      this.uploadQueue.next({ key, orderDocumentId, scopeKey: currentScopeKey, file, kind });
    }
  }

  private upload(job: UploadJob): Observable<unknown> {
    return this.pdfFile(job).pipe(
      tap(() => this.updateUpload(job.key, { converting: false })),
      switchMap(pdf =>
        this.orderDocumentService.uploadDocument(job.orderDocumentId, pdf).pipe(
          tap(event => {
            if (event.type === HttpEventType.UploadProgress) {
              const progress = Math.round((100 * event.loaded) / (event.total ?? pdf.size));
              this.updateUpload(job.key, { progress });
            } else if (event.type === HttpEventType.Response) {
              // Skip the list if the page has switched to another order (or unlinked one) meanwhile
              if (job.scopeKey === scopeKey(this.scope())) this.mergeDocuments(event.body?.DATA ?? []);
              this.dismissUpload(job.key);
            }
          })
        )
      ),
      catchError((error: unknown) => {
        const message =
          error instanceof ConversionError
            ? error.userMessage
            : error instanceof HttpErrorResponse
              ? this.uploadErrorMessage(error)
              : 'Upload fehlgeschlagen';
        this.updateUpload(job.key, { converting: false, error: message });
        return EMPTY;
      })
    );
  }

  // The file as PDF: images are converted first. Runs inside the upload queue, so at most
  // UPLOAD_CONCURRENCY conversions/uploads are busy at once.
  private pdfFile(job: UploadJob): Observable<File> {
    if (job.kind === 'pdf') return of(job.file);
    return from(this.imagePdfConverter.toPdf(job.file)).pipe(
      catchError(() => throwError(() => new ConversionError('Bild konnte nicht umgewandelt werden'))),
      switchMap(pdf =>
        pdf.size > MAX_DOCUMENT_SIZE_BYTES ? throwError(() => new ConversionError(`PDF zu groß (max. ${MAX_DOCUMENT_SIZE_LABEL})`)) : of(pdf)
      )
    );
  }

  // Mapped by status: a proxy's 413 has an HTML body, not our JSON.
  private uploadErrorMessage(error: HttpErrorResponse): string {
    switch (error.status) {
      case 0:
        return 'Verbindung abgebrochen';
      case 400:
        return typeof error.error?.MESSAGE === 'string' ? error.error.MESSAGE : 'Keine gültige PDF-Datei';
      case 403:
        return 'Keine Berechtigung zum Hochladen';
      case 404:
        return 'Auftragsbegleitschein nicht gefunden';
      case 413:
        return `Datei zu groß (max. ${MAX_DOCUMENT_SIZE_LABEL})`;
      default:
        return 'Upload fehlgeschlagen';
    }
  }

  private loadDocuments(scope: DocumentsScope): Observable<IOrderDocumentFile[]> {
    this.isLoading.set(true);
    const request =
      scope.kind === 'order'
        ? this.orderDocumentService.getDocumentsByOrderId(scope.id)
        : this.orderDocumentService.getDocuments(scope.id);
    return request.pipe(
      map(response => response.DATA ?? []),
      catchError(() => {
        this.toastService.showError('Dokumente konnten nicht geladen werden');
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  private reload(): void {
    const scope = this.scope();
    if (!scope) return;
    this.loadDocuments(scope).subscribe(documents => {
      if (scopeKey(scope) === scopeKey(this.scope())) this.documents.set(documents);
    });
  }

  // Newest first, like the API
  private mergeDocuments(added: IOrderDocumentFile[]): void {
    const addedIds = new Set(added.map(doc => doc._id));
    this.documents.update(list =>
      [...added, ...list.filter(doc => !addedIds.has(doc._id))].sort((a, b) => b.uploadDate.localeCompare(a.uploadDate))
    );
  }

  private removeDocument(fileId: string): void {
    this.documents.update(list => list.filter(doc => doc._id !== fileId));
  }

  private updateUpload(key: number, changes: Partial<UploadItem>): void {
    this.uploads.update(list => list.map(item => (item.key === key ? { ...item, ...changes } : item)));
  }
}
